import type { BreadcrumbList, ListItem } from 'schema-dts'
import { breadcrumbListId } from '../../utils/structuredData/helpers/ids'
import type { BlockStructuredDataResolver } from '../../utils/structuredData/types'
import { resolveBreadcrumbTrail } from './breadcrumbTrail'

/**
 * Endereço absoluto de um nível da trilha.
 *
 * Devolve `undefined` — e não um palpite — quando o link está vazio ou quando
 * sobrou template sem resolver: `ListItem` sem `item` continua sendo um nível
 * válido da trilha, mas apontando para `{{item.slug}}` seria URL inválida. É a
 * mesma guarda do `resolveListingLinks`.
 */
function absoluteLink({
  baseUrl,
  link,
}: {
  baseUrl: string
  link?: string
}): string | undefined {
  const trimmed = link?.trim()
  if (!trimmed || trimmed.includes('{{')) return undefined

  if (/^https?:\/\//i.test(trimmed)) return trimmed

  return `${baseUrl}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`
}

/** Mínimo que o Google exige para o recurso de breadcrumb. */
const MIN_ITEMS = 2

/**
 * A trilha de navegação do bloco, como `BreadcrumbList`.
 *
 * O `variant` separa as duas variações do bloco: só a dinâmica resolve
 * `{{item.name|text}}` nos rótulos e nos links, e ele também entra no `@id`.
 *
 * A limpeza mora aqui, e não no `resolveBreadcrumbTrail`: aquele descreve o que
 * a página renderiza, e as regras abaixo são de **emissão**. Item sem rótulo é
 * renderizado normalmente, mas não vira `ListItem` — `ListItem` sem `name` não
 * descreve nada.
 */
const makeBreadcrumbResolver = ({
  variant,
}: {
  variant: 'static' | 'dynamic'
}): BlockStructuredDataResolver => ({ nodeId, props, structure, context }) => {
  const trail = resolveBreadcrumbTrail({
    items: props.items,
    structure: variant === 'dynamic' ? structure : undefined,
  }).filter((item) => item.text?.trim())

  if (trail.length < MIN_ITEMS) return []

  const itemListElement = trail.map((item, index): ListItem => {
    // A página atual não precisa apontar para si mesma.
    const url = item.isCurrent
      ? undefined
      : absoluteLink({ baseUrl: context.baseUrl, link: item.link })

    return {
      '@type': 'ListItem',
      position: index + 1,
      name: item.text,
      ...(url ? { item: url } : {}),
    }
  })

  const node: BreadcrumbList = {
    '@type': 'BreadcrumbList',
    '@id': breadcrumbListId({
      canonicalUrl: context.canonicalUrl,
      channelPublicId: context.channelPublicId,
      nodeId,
      variant,
    }),
    itemListElement,
  }

  return [node]
}

export const breadcrumbStructuredData = makeBreadcrumbResolver({
  variant: 'static',
})

export const breadcrumbDynamicStructuredData = makeBreadcrumbResolver({
  variant: 'dynamic',
})
